import { AutoComplete, Button, Checkbox, Drawer, Form, Input, Radio, Select, Spin, notification } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { buyACourse, getAllCoursesByRole, getCourseById } from '../../../../Constants/Api/functions/function';
import { useSelector } from 'react-redux';

const { Option } = Select;

const AddPayment = ({ setOpen, isOpen, hadleGetPayments, users }) => {
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(false)
    const [isInstallmentLoading, setIsInstallmentLoading] = useState(false)
    const [api, contextHolder] = notification.useNotification();
    const [isAddToPayments, setIsAddToPayments] = useState(false)
    const [courses, setCourses] = useState([])
    const [selectedCourse, setSelectedCourse] = useState(null)
    const [courseInstallments, setCourseInstallments] = useState([])
    const allCources = useSelector(state => state.allCources.allCources);
    const userProfile = useSelector(state => state.userProfile.userProfile);

    useEffect(() => {
        if (allCources) {
            if (userProfile?.role === 'coordinator') {
                setCourses(allCources.filter(c => c.coordinatorId && String(c.coordinatorId) === String(userProfile.id)));
            } else if (userProfile?.role === 'instructor') {
                setCourses(allCources.filter(c => c.instructorId && String(c.instructorId) === String(userProfile.id)));
            } else {
                setCourses(allCources);
            }
        } else {
            hadleGetCources();
        }
    }, [allCources, isOpen, userProfile]);

    const hadleGetCources = async () => {
        setIsLoading(true);
        if (userProfile) {
            const response = await getAllCoursesByRole(userProfile.role, userProfile.id);
            if (response) {
                setCourses(response);
            }
        }
        setIsLoading(false);
    }


    const installmentOptions = useMemo(() => {
        if (courseInstallments?.length > 0) {
            return courseInstallments;
        }

        if (selectedCourse?.installments) {
            const totalInstallments = Number(selectedCourse.installments) || 0;
            const pricePerInstallment =
                selectedCourse?.price && totalInstallments
                    ? Number(selectedCourse.price) / totalInstallments
                    : 0;

            return Array.from({ length: totalInstallments }, (_, index) => ({
                installmentNumber: index + 1,
                amount: pricePerInstallment
            }));
        }

        return [];
    }, [courseInstallments, selectedCourse]);

    const fetchCourseInstallments = useCallback(async (courseId) => {
        if (!courseId) {
            setCourseInstallments([]);
            return;
        }

        setIsInstallmentLoading(true);
        try {
            let courseData = await getCourseById(courseId);
            if (Array.isArray(courseData)) {
                courseData = courseData[0];
            }

            if (courseData) {
                setSelectedCourse(prev => ({
                    ...prev,
                    ...courseData
                }));

                if (courseData.installmentBreakdown?.length) {
                    setCourseInstallments(courseData.installmentBreakdown);
                } else {
                    setCourseInstallments([]);
                }
            } else {
                setCourseInstallments([]);
            }
        } catch (error) {
            console.error('fetchCourseInstallments', error);
            setCourseInstallments([]);
        } finally {
            setIsInstallmentLoading(false);
        }
    }, []);

    const onFinish = async (values) => {
        setIsLoading(true)

        if (values.isAddToPayments) {
            const selectedInstallments = (values.payedInstallments || []).map(
                installmentNumber => Number(installmentNumber)
            );

            const installmentsForSubmission = selectedInstallments
                .map(installmentNumber =>
                    installmentOptions.find(
                        installment => installment.installmentNumber === installmentNumber
                    )
                )
                .filter(Boolean)
                .map(installment => ({
                    installmentNumber: installment.installmentNumber,
                    amount: Number(installment.amount) || 0
                }));

            const calculatedAmount = installmentsForSubmission.reduce(
                (sum, installment) => sum + installment.amount,
                0
            );

            values.amount = calculatedAmount;
            values.paymentAmount = calculatedAmount.toFixed(2);
            values.installmentPayments = installmentsForSubmission;
            values.payedInstallments = installmentsForSubmission.length;
        } else {
            values.isPaid = false;
            values.isAddToPayments = false;
            values.payedInstallments = 0;
            values.installmentPayments = [];
        }

        try {
            const response = await buyACourse(values);
            if (response) {
                hadleGetPayments();
                api.success({
                    message: 'User added to course'
                })
            }
        } catch (error) {
            console.error('onFinish', error);
            api.error({
                message: 'Failed to add user to course'
            })
        }
        setIsLoading(false)
        setOpen(false)
    }

    return (
        <div>
            {contextHolder}
            <Drawer
                title="Add Course to User"
                width={720}
                onClose={() => setOpen(false)}
                open={isOpen}
                styles={{
                    body: {
                        paddingBottom: 80,
                    },
                }}
            >
                <Spin
                    spinning={isLoading || isInstallmentLoading}
                    tip="Please wait..."
                >
                    <Form
                        layout="vertical"
                        onFinish={onFinish}
                        form={form}
                    >
                        <Form.Item
                            name='userId'
                            label='User'
                            rules={[
                                {
                                    required: true,
                                    message: 'Please select user'
                                },
                            ]}
                        >
                            <Select
                                placeholder='Select user'
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {users?.map(user => (
                                    <Option key={user.id} value={user.id}>{user.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name='courseId'
                            label='Course'
                            rules={[
                                {
                                    required: true,
                                    message: 'Please select course'
                                },
                            ]}
                        >
                            <Select
                                placeholder='Select course'
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                                onChange={(value) => {
                                    const course = courses.find(c => c.id === value);
                                    setSelectedCourse(course);
                                    form.setFieldsValue({
                                        payedInstallments: [],
                                        paymentAmount: undefined
                                    });
                                    fetchCourseInstallments(value);
                                }}
                            >
                                {courses?.map(course => (
                                    <Option key={course.id} value={course.id}>{course.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name='isAddToPayments'
                            label='Add to payments'
                            rules={[
                                {
                                    required: true,
                                    message: 'Please select add to payments'
                                },
                            ]}
                        >
                            <Radio.Group
                                onChange={(e) => setIsAddToPayments(e.target.value)}
                            >
                                <Radio value={true}>Yes</Radio>
                                <Radio value={false}>No</Radio>
                            </Radio.Group>
                        </Form.Item>

                        {isAddToPayments && <>
                            <Form.Item
                                name='isPaid'
                                label='Is Paid'
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please select is paid'
                                    },
                                ]}
                            >
                                <Radio.Group>
                                    <Radio value={true}>Yes</Radio>
                                    <Radio value={false}>No</Radio>
                                </Radio.Group>
                            </Form.Item>

                            <Form.Item
                                name='paymentStatus'
                                label='Payment Status'
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please select payment status'
                                    },
                                ]}
                            >
                                <Radio.Group>
                                    <Radio value='pending'>Pending</Radio>
                                    <Radio value='paid'>Completed</Radio>
                                </Radio.Group>
                            </Form.Item>
                            

                            <Form.Item
                                name='payedInstallments'
                                label='Paid Installments'
                                rules={[
                                    {
                                        required: true,
                                        type: 'array',
                                        min: 1,
                                        message: 'Please select at least one installment'
                                    },
                                ]}
                            >
                                <Checkbox.Group
                                    onChange={(selectedValues = []) => {
                                        const numericSelections = selectedValues.map(value => Number(value));

                                        const amountMap = installmentOptions.reduce((acc, installment) => {
                                            acc.set(
                                                installment.installmentNumber,
                                                parseFloat(installment.amount) || 0
                                            );
                                            return acc;
                                        }, new Map());

                                        const totalAmount = numericSelections.reduce(
                                            (sum, value) => sum + (amountMap.get(value) || 0),
                                            0
                                        );

                                        form.setFieldsValue({
                                            payedInstallments: numericSelections,
                                            paymentAmount: totalAmount.toFixed(2)
                                        });
                                    }}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {installmentOptions.map(installment => (
                                            <Checkbox
                                                key={installment.installmentNumber}
                                                value={installment.installmentNumber}
                                            >
                                                {`Installment ${installment.installmentNumber} - Amount: LKR ${(
                                                    parseFloat(installment.amount) || 0
                                                ).toFixed(2)}`}
                                            </Checkbox>
                                        ))}
                                    </div>
                                </Checkbox.Group>
                            </Form.Item>

                            <Form.Item
                                name='paymentAmount'
                                label='Payment Amount'
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter payment amount'
                                    },
                                ]}
                            >
                                <Input
                                    type='number'
                                    placeholder='Calculated payment amount'
                                    readOnly
                                />
                            </Form.Item>

                            <Form.Item
                                name='paymentMethod'
                                label='Payment Method'
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please select payment method'
                                    },
                                ]}
                            >
                                <Select
                                    placeholder='Select payment method'
                                >
                                    <Option value='cash'>Cash</Option>
                                    <Option value='card'>Card</Option>
                                    <Option value='bank'>Bank</Option>
                                </Select>
                            </Form.Item>
                        </>}

                        <Form.Item>
                            <Button
                                type='primary'
                                htmlType='submit'
                            >
                                Add User
                            </Button>
                        </Form.Item>
                    </Form>
                </Spin>
            </Drawer>
        </div>
    )
}
export default AddPayment
